/**
 * 将图片URL转换为Base64编码
 * @param src 图片URL
 * @returns Promise<string> Base64编码的图片数据
 */
export const imageToBase64 = async (src: string): Promise<string> => {
    if (src.startsWith('data:')) return src;
    
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL());
      };
      img.onerror = reject;
      img.src = src;
    });
  };
  
  /**
   * 将DOM元素转换为SVG字符串
   * @param element HTML元素
   * @param selectedStyle 选中的样式
   * @returns Promise<string> SVG字符串
   */
  export const createSvgFromElement = async (
    element: HTMLElement,
    selectedStyle: any
  ): Promise<string> => {
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    const style = window.getComputedStyle(element);
  
    // 获取字体数据
    try {
      const response = await fetch('/fonts/fonts_base64.json');
      const fontData = await response.json();
  
      // 获取所有使用的字体
      const usedFonts = [
        selectedStyle.titleFont,
        selectedStyle.coupleNamesFont,
        selectedStyle.dateFont,
        selectedStyle.tableFont,
        selectedStyle.guestListFont
      ];
  
      // 创建SVG
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", width.toString());
      svg.setAttribute("height", height.toString());
  
      const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
      const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  
      // 从fontData中获取字体数据
      const titleFontData = fontData[selectedStyle.titleFont];
      const coupleNamesFontData = fontData[selectedStyle.coupleNamesFont];
      const dateFontData = fontData[selectedStyle.dateFont];
      const tableFontData = fontData[selectedStyle.tableFont];
      const guestListFontData = fontData[selectedStyle.guestListFont];
  
      style.textContent = `
        @font-face {
          font-family: '${selectedStyle.titleFont}';
          src: url(${titleFontData}) format('truetype');
        }
        @font-face {
          font-family: '${selectedStyle.coupleNamesFont}';
          src: url(${coupleNamesFontData}) format('truetype');
        }
        @font-face {
          font-family: '${selectedStyle.dateFont}';
          src: url(${dateFontData}) format('truetype');
        }
        @font-face {
          font-family: '${selectedStyle.tableFont}';
          src: url(${tableFontData}) format('truetype');
        }
        @font-face {
          font-family: '${selectedStyle.guestListFont}';
          src: url(${guestListFontData}) format('truetype');
        }
        .chart-title {
          font-family: '${selectedStyle.titleFont}';
          font-size: ${selectedStyle.titleSize};
          text-align: center;
          margin-bottom: 20px;
        }
        .couple-names {
          font-family: '${selectedStyle.coupleNamesFont}';
          font-size: ${selectedStyle.coupleNamesSize};
          text-align: center;
          margin-bottom: 10px;
        }
        .wedding-date {
          font-family: '${selectedStyle.dateFont}';
          font-size: ${selectedStyle.dateSize};
          text-align: center;
          margin-bottom: 30px;
        }
        .guest-list-table-name {
          font-family: '${selectedStyle.tableFont}';
          font-size: ${selectedStyle.tableFontSize};
        }
        .guest-item {
          font-family: '${selectedStyle.guestListFont}';
          font-size: ${selectedStyle.guestListFontSize};
        }
      `;
  
      defs.appendChild(style);
      svg.appendChild(defs);
  
      // 创建foreignObject
      const foreignObject = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
      foreignObject.setAttribute("width", width.toString());
      foreignObject.setAttribute("height", height.toString());
      foreignObject.setAttribute("x", "0");
      foreignObject.setAttribute("y", "0");
  
      // 创建div
      const div = document.createElementNS("http://www.w3.org/1999/xhtml", "div");
      div.setAttribute("style", `
        width: ${width}px;
        height: ${height}px;
        background: white;
        display: flex;
        flex-direction: column;
        align-items: center;
      `);
  
      // 创建header-section
      const headerSection = document.createElementNS("http://www.w3.org/2000/svg", "div");
      headerSection.setAttribute("class", "header-section");
  
      // 创建chart-title
      const chartTitle = document.createElement("div");
      chartTitle.setAttribute("class", "chart-title");
      chartTitle.textContent = element.querySelector(".chart-title")?.textContent || "Wedding Seating Chart";
  
      // 创建couple-names
      const coupleNamesDiv = document.createElement("div");
      coupleNamesDiv.setAttribute("class", "couple-names");
      coupleNamesDiv.textContent = element.querySelector(".couple-names")?.textContent || "";
  
      // 创建wedding-date
      const weddingDateDiv = document.createElement("div");
      weddingDateDiv.setAttribute("class", "wedding-date");
      weddingDateDiv.textContent = element.querySelector(".wedding-date")?.textContent || "";
  
      // 创建guest-list
      const guestList = document.createElement("div");
      guestList.setAttribute("class", "guest-list");
      guestList.innerHTML = element.innerHTML;
  
      // 添加子元素到div
      div.appendChild(headerSection);
      headerSection.appendChild(chartTitle);
      headerSection.appendChild(coupleNamesDiv);
      headerSection.appendChild(weddingDateDiv);
      div.appendChild(guestList);
  
      // 添加div到foreignObject
      foreignObject.appendChild(div);
  
      // 添加foreignObject到svg
      svg.appendChild(foreignObject);
  
      return svg.outerHTML;
    } catch (error) {
      console.error("创建SVG时发生错误:", error);
      throw error;
    }
  };